/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import apiService from "services/apiService";
import Select from "react-select";
import Loader from "components/Loader";
// Admin-configured options (Asset/Device/Status) + file upload policy (types/size).
import { useInventoryOptions, useInventoryUploadConfig, normalizeAssetType } from "./useInventoryConfig";
// Reusable uploader: dropzone + file info + shared MediaPreview lightbox.
import { InventoryImageUploader } from "./InventoryImageUploader";
import { sortByKey } from "util/sortOptions";
import { useToaster } from "contexts/toasterContext";
import { colors } from "themes";
import { customSelectStyles } from "util/selectStyles";
import { handleNameInput } from "util/Util";

interface OptionProps {
  value: string;
  label: string;
}

const toDateOnly = (value: unknown): Date | null => {
  if (!value) return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === "string") {
    const parsed = new Date(`${value}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) {
      return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }

  const parsed = new Date(value as any);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const toDateInputValue = (value: unknown): string => {
  if (!value) return "";

  if (typeof value === "string") {
    const matchedDate = value.match(/\d{4}-\d{2}-\d{2}/);
    if (matchedDate?.[0]) return matchedDate[0];
  }

  const parsed = new Date(value as any);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const toPayloadDate = (value: unknown): string | null => {
  const parsed = toDateOnly(value);
  if (!parsed) return null;

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const schema = yup.object().shape({
  assign: yup.string().required("Assign is required"),
  type: yup.string().required("Type is required"),
  status: yup.string().required("Status is required"),
  owner: yup.string().required("Owner is required"),
  location: yup.string().required("Location is required"),
  identity: yup.string().required("Inventory Identity Number is required"),
  model: yup.string().required("Model is required"),
  brand: yup.string().required("Brand is required"),
  assignDate: yup
    .date()
    .required("Assign Date is required")
    .test(
      "assign-gte-purchase",
      "Assign Date should be same or after Purchase Date",
      function (assignDate) {
        const purchaseDate = this.parent.purchaseDate;
        if (!assignDate || !purchaseDate) return true;

        const assign = toDateOnly(assignDate);
        const purchase = toDateOnly(purchaseDate);
        if (!assign || !purchase) return true;

        return assign >= purchase;
      }
    ),
  purchaseDate: yup
    .date()
    .required("Purchase Date is required")
    .test(
      "purchase-lte-assign",
      "Purchase Date should be before or same as Assign Date",
      function (purchaseDate) {
        const assignDate = this.parent.assignDate;
        if (!purchaseDate || !assignDate) return true;

        const purchase = toDateOnly(purchaseDate);
        const assign = toDateOnly(assignDate);
        if (!purchase || !assign) return true;

        return purchase <= assign;
      }
    )
    .test(
      "purchase-lte-release",
      "Purchase Date should be before or same as Release Date",
      function (purchaseDate) {
        const releaseDate = this.parent.releaseDate;
        if (!purchaseDate || !releaseDate) return true;

        const purchase = toDateOnly(purchaseDate);
        const release = toDateOnly(releaseDate);
        if (!purchase || !release) return true;

        return purchase <= release;
      }
    ),
  releaseDate: yup
    .date()
    .required("Release Date is required")
    .test(
      "release-gte-purchase",
      "Release Date should be same or after Purchase Date",
      function (releaseDate) {
        const purchaseDate = this.parent.purchaseDate;
        if (!releaseDate || !purchaseDate) return true;

        const release = toDateOnly(releaseDate);
        const purchase = toDateOnly(purchaseDate);
        if (!release || !purchase) return true;

        return release >= purchase;
      }
    ),
  asset_type: yup.string().required("Inventorytype is required"),
  userEmail: yup.string().required("User Email is required"),
  cost: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "own",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Cost Value is required")
        .typeError("Please enter a valid number for Cost Value")
        .min(0, "Cost must be a non-negative number"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  depreciationRate: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "own",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Depreciation Rate is required")
        .typeError("Please enter a valid number for Depreciation Rate")
        .min(0, "Depreciation Rate must be a non-negative number")
        .max(100, "Depreciation rate cannot be greater than 100"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  rentAmount: yup.number().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "rental",
    then: (schema) =>
      schema
        .transform((value, originalValue) =>
          originalValue === "" || originalValue === null || originalValue === undefined
            ? undefined
            : value
        )
        .required("Rental Amount is required")
        .typeError("Please enter a valid number for rent amount")
        .min(0, "Rent Amount must be a non-negative number"),
    otherwise: (schema) =>
      schema.transform((value, originalValue) =>
        originalValue === "" || originalValue === null || originalValue === undefined
          ? undefined
          : value
      ),
  }),
  rentStartDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "rental",
    then: (schema) => schema.required("Assign Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  issueDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "sample",
    then: (schema) => schema.required("Issued Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
  returnDate: yup.string().when("asset_type", {
    is: (val: string) => normalizeAssetType(val) === "sample",
    then: (schema) => schema.required("Return Date is required"),
    otherwise: (schema) => schema.notRequired(),
  }),
});
interface editProductProps {
  onClose: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setRefresh: any;
  refresh: number;
}
const EditInventory = ({
  onClose,
  data,
  setRefresh,
  refresh,
}: editProductProps) => {
  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      ...data,
    },
  });
  const [option, setOption] = useState("");
  const [data1, setData] = useState<OptionProps[]>([]);
  const [selected, setSelected] = useState<OptionProps>();
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false);
  console.log("isSubmitting :>> ", isSubmitting);
  const [usersInitialized, setUsersInitialized] = useState(false);
  const [device, setDevice] = useState("");
  const [step, setStep] = useState(0);
  const [images, setImages] = useState<File[]>(data.urls || []);
  // Inline file-validation error (wrong format / size / count). Kept in state so
  // it persists and can block submission until the user corrects the selection.
  const [fileError, setFileError] = useState("");
  const [status, setStatus] = useState<any>();
  const [userPage, setUserPage] = useState(0);
  const [userSearch, setUserSearch] = useState("");
  const [hasMoreUsers, setHasMoreUsers] = useState(true);

  const USER_PAGE_SIZE = 10;
  const { addToast } = useToaster();

  // Admin-configured dropdown options + file upload policy (types/size/count).
  const { assetTypeOptions, deviceTypeOptions, deviceStatusOptions } = useInventoryOptions();
  const uploadConfig = useInventoryUploadConfig();

  // Resolve the saved asset type (from the edit record) against the admin-config
  // options. Matched case-insensitively (the API may return "RENTAL" etc.) with a
  // fallback to the raw value, so the default shows on open even while options load.
  const watchedAssetType = methods.watch("asset_type");
  const assetTypeValue =
    assetTypeOptions.find(
      (o) => normalizeAssetType(o.value) === normalizeAssetType(watchedAssetType),
    ) ??
    (watchedAssetType ? { value: watchedAssetType, label: watchedAssetType } : null);

  const stepOneRequiredFields = [
    "model",
    "brand",
    "type",
    "location",
    "owner",
    "assign",
    "status",
    "identity",
  ] as const;
  type StepOneField = (typeof stepOneRequiredFields)[number];

  const nextStep = async () => {
    const isStepOneValid = await methods.trigger(stepOneRequiredFields, {
      shouldFocus: true,
    });

    if (!isStepOneValid) {
      return;
    }

    setStep((prev) => prev + 1);
  };
  const prevStep = () => setStep((prev) => prev - 1);

  useEffect(() => {
    const subscription = methods.watch((values, { name }) => {
      if (name === "assignDate" || name === "releaseDate" || name === "purchaseDate") {
        void methods.trigger("purchaseDate");
      }

      if (
        step === 0 &&
        name &&
        (stepOneRequiredFields as readonly string[]).includes(name)
      ) {
        const fieldName = name as StepOneField;
        const fieldValue = values[fieldName];
        const hasValue =
          typeof fieldValue === "string"
            ? fieldValue.trim() !== ""
            : fieldValue !== undefined && fieldValue !== null && fieldValue !== "";

        if (hasValue) {
          methods.clearErrors(fieldName);
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [methods, step]);

  const removeImage = (indexToRemove: number) => {
    setImages((prevImages) =>
      prevImages.filter((_, index) => index !== indexToRemove)
    );
  };

  // Add one or more files (existing images + new files) with admin-config
  // validation (count, allowed types, max size).
  const handleAddFiles = (files: FileList) => {
    const fileArray = Array.from(files);
    if (!fileArray.length) return;
    if (images.length + fileArray.length > uploadConfig.maxFiles) {
      setFileError(`You can upload a maximum of ${uploadConfig.maxFiles} files.`);
      return;
    }
    for (const file of fileArray) {
      const error = uploadConfig.validateFile(file);
      if (error) {
        setFileError(error);
        return;
      }
    }
    setFileError("");
    setImages((prev: any) => [...prev, ...fileArray]);
  };

  const isEmpty = (obj: any) => {
    return Object.keys(obj).length === 0;
  };

  const getUsersFromPayload = (payload: any) => {
    if (Array.isArray(payload)) {
      return payload;
    }

    if (Array.isArray(payload?.content)) {
      return payload.content;
    }

    return [];
  };

  const fetchUsers = async (page: number, searchValue: string, append: boolean) => {
    if (loadingUsers) return;

    setLoadingUsers(true);
    try {
      const query = new URLSearchParams();
      query.set("page", String(page));
      query.set("size", String(USER_PAGE_SIZE));
      if (searchValue.trim()) {
        query.set("search", searchValue.trim());
      }

      const res = await apiService.get(`api/users?${query.toString()}`);
      const payload = res.data;
      const users = getUsersFromPayload(payload);

      const options = users.map((email: any) => ({
        value: email.email,
        label: email.displayName,
      }));

      setData((prev) => {
        const merged = append ? [...prev, ...options] : options;
        const dedup = new Map<string, OptionProps>();
        merged.forEach((item) => {
          if (!dedup.has(item.value)) {
            dedup.set(item.value, item);
          }
        });
        return Array.from(dedup.values());
      });

      setUserPage(page);
      if (typeof payload?.last === "boolean") {
        setHasMoreUsers(!payload.last);
      } else if (typeof payload?.totalPages === "number") {
        setHasMoreUsers(page < payload.totalPages - 1);
      } else {
        setHasMoreUsers(options.length === USER_PAGE_SIZE);
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const getDevicePayload = (formData: any, deviceType: string) => ({
    ownedDevice:
      normalizeAssetType(deviceType) === "own"
        ? {
          id: formData.ownedDevice?.id ?? null,
          purchaseDate: toPayloadDate(formData.purchaseDate),
          cost: formData.cost ?? 0,
          s3InvoiceUrl: formData.ownedDevice?.s3InvoiceUrl ?? null,
          depreciationRate: formData.depreciationRate ?? 0,
          usefulLife: formData.ownedDevice?.usefulLife ?? 0,
        }
        : null,

    rentedDevice:
      normalizeAssetType(deviceType) === "rental"
        ? {
          id: formData.rentedDevice?.id ?? null,
          rentStartDate: formData.rentStartDate ?? null,
          rentAmount: formData.rentAmount ?? 0,
        }
        : null,

    sampleDevice:
      normalizeAssetType(deviceType) === "sample"
        ? {
          id: formData.sampleDevice?.id ?? null,
          issueDate: formData.issueDate ?? null,
          returnDate: formData.returnDate ?? null,
        }
        : null,
  });

  const buildAssetPayload = (
    formData: any,
    deviceType: string,
    keptImages: any[]
  ) => ({
    id: formData.id,
    model: formData.model,
    brand: formData.brand,
    config: formData.config ?? null,
    ...getDevicePayload(formData, deviceType),
    purchaseDate: toPayloadDate(formData.purchaseDate),
    validityFrom: formData.validityFrom ?? null,
    validityTo: formData.validityTo ?? null,
    assign: formData.assign,
    assignDate: toPayloadDate(formData.assignDate),
    releaseDate: toPayloadDate(formData.releaseDate),
    type: formData.type,
    status: formData.status,
    owner: formData.owner,
    location: formData.location,
    identityType: formData.identityType ?? null,
    identity: formData.identity ?? null,
    createdBy: formData.createdBy,
    created: formData.created,
    modifiedBy: formData.modifiedBy,
    modified: formData.modified,
    assetPassword: formData.assetPassword,
    userEmail: formData.userEmail,
    urls: keptImages.map((img: any) => ({ id: img.id })),
    asset_type: formData.asset_type,
  });

  const getStorageType = async () => {
    try {
      const storageRes = await apiService.get("api/admin/storage");
      return storageRes?.data?.storage || "DRIVE";
    } catch (error) {
      console.log("Failed to detect storage type, using DRIVE flow", error);
      return "DRIVE";
    }
  };

  const uploadFilesToS3 = async (files: File[]) => {
    const uploadedKeys: string[] = [];

    for (const file of files) {
      const uploadUrlRes = await apiService.post("api/upload-url", {
        fileName: file.name,
      });

      const { uploadUrl, key } = uploadUrlRes?.data || {};

      if (!uploadUrl || !key) {
        throw new Error("Missing uploadUrl or key from upload-url response");
      }

      const putResponse = await fetch(uploadUrl, {
        method: "PUT",
        body: file,
      });

      if (!putResponse.ok) {
        throw new Error("File upload to storage failed");
      }

      uploadedKeys.push(key);
    }

    return uploadedKeys;
  };

  const appendFilesToFormData = async (
    formData: FormData,
    files: File[]
  ) => {
    if (!files.length) return;

    const storageType = await getStorageType();

    if (storageType === "S3") {
      const uploadedKeys = await uploadFilesToS3(files);

      uploadedKeys.forEach((key) => {
        formData.append("keys", key);
      });

      return;
    }

    files.forEach((file) => {
      formData.append("files", file);
    });
  };

  const onSubmit: SubmitHandler<any> = async (formDataValues: any) => {
    // Guard: never submit while an unresolved file validation error is active.
    if (fileError) {
      return;
    }

    setLoading(true);

    try {
      const deviceType = formDataValues.asset_type;

      const keptImages = images.filter((img: any) => img.id);

      const newFiles = images.filter(
        (img: any) => img instanceof File
      ) as File[];

      const assetPayload = buildAssetPayload(
        formDataValues,
        deviceType,
        keptImages
      );

      const formData = new FormData();

      formData.append(
        "asset",
        new Blob([JSON.stringify(assetPayload)], {
          type: "application/json",
        })
      );

      await appendFilesToFormData(formData, newFiles);

      await apiService.put("/api/assets/asset", formData);

      setRefresh(refresh + 1);
      onClose();

      addToast("Inventory Updated Successfully", "success");
    } catch (err: any) {
      console.log("Update inventory failed", err);

      const errorMessage =
        err?.response?.data ||
        err?.response?.data?.message ||
        err?.message ||
        "Something Went Wrong";

      addToast(errorMessage, "error");
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const steps = [
    <div key="step1" className="grid gap-4 sm:grid-cols-3">
      <Input name="model" label="Model" placeholder="Enter Model" required />
      <Input name="brand" label="Brand" placeholder="Enter Brand" required />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Select Device Type <span className="text-red-500"> *</span>
        </label>
        <Select
          {...methods.register("type", { required: true })}
          options={sortByKey(deviceTypeOptions, "label")}
          placeholder="Device Type"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          onChange={(selectedOption: any) => {
            if (selectedOption) {
              methods.setValue("type", selectedOption.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              setDevice(selectedOption.value);
            }
          }}
          value={deviceTypeOptions.find((opt) => opt.value === device)}
          defaultValue={deviceTypeOptions.find((opt) => opt.value === device)}
        />
        {methods.formState.errors.type && (
          <span className="text-red-500 text-sm mt-1">
            {methods.formState.errors.type.message as string}
          </span>
        )}
      </div>
      <Input
        name="location"
        label="Location"
        placeholder="Enter Location"
        required
      />
      <Input
        name="owner"
        label="Owner"
        placeholder="Enter Owner Name"
        required
        onInput={(e: React.FormEvent<HTMLInputElement>) =>
          handleNameInput(e, methods.setValue)
        }
      />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Assign To<span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("assign", { required: true })}
          options={data1}
          placeholder="Select Employee"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          isLoading={loadingUsers}
          onMenuScrollToBottom={() => {
            if (hasMoreUsers && !loadingUsers) {
              void fetchUsers(userPage + 1, userSearch, true);
            }
          }}
          onInputChange={(value, actionMeta) => {
            if (actionMeta.action === "input-change") {
              setUserSearch(value);
            }
            if (actionMeta.action === "menu-close") {
              setUserSearch("");
            }
            return value;
          }}
          onChange={(selectedOption) => {
            if (selectedOption) {
              methods.setValue("assign", selectedOption.label, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              methods.setValue("userEmail", selectedOption.value, {
                shouldDirty: true,
                shouldTouch: true,
              });
              setSelected(selectedOption);
            }
          }}
          value={selected}
        />
        {methods.formState.errors.assign && (
          <span className="text-red-500 text-sm mt-1">
            {methods.formState.errors.assign.message as string}
          </span>
        )}
      </div>
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Status<span className="text-red-500">*</span>
        </label>
        <Select
          {...methods.register("status", { required: true })}
          options={deviceStatusOptions}
          placeholder="Select Status"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          onChange={(selectedOption) => {
            if (selectedOption) {
              methods.setValue("status", selectedOption.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              // NOTE: Status must NOT write `option` — that state drives the
              // asset-type conditional fields (Own/Rental/Sample) below.
              setStatus(selectedOption);
            }
          }}
          defaultValue={deviceStatusOptions.find((opt) =>
            opt.value === data.status ? data.status : ""
          )}
          value={status}
        />
        {methods.formState.errors.status && (
          <span className="text-red-500 text-sm font-semibold mt-1">
            {methods.formState.errors.status.message as string}
          </span>
        )}
      </div>

      <Input
        name="assetPassword"
        label="Asset Password"
        placeholder="Enter Asset password"
      />
      <Input
        name="identity"
        type="text"
        label="Inventory Identity Number"
        placeholder="Enter Identity Number"
        required
      />
    </div>,
    <div key="step2" className="grid gap-4 sm:grid-cols-3">
      <Input
        name="userEmail"
        label="User Email"
        type="email"
        placeholder="Enter User Email"
        disabled
      />
      <Input
        name="assignDate"
        type="date"
        label="Assign Date"
        placeholder="Enter Assign Date"
        required
      />
      <Input
        name="purchaseDate"
        type="date"
        label="Purchase Date"
        placeholder="Enter Purchase Date"
        required
      />
      <Input
        name="releaseDate"
        type="date"
        label="Release Date"
        placeholder="Enter Release date"
        required
      />
      <div className="mb-4">
        <label className="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
          Asset Type <span className="text-red-500"> *</span>
        </label>
        <Select
          {...methods.register("asset_type", { required: true })}
          options={assetTypeOptions}
          placeholder="Asset Type"
          className="w-full text-slate-900 dark:text-slate-100"
          styles={customSelectStyles}
          onChange={(selectedOption) => {
            if (selectedOption) {
              methods.setValue("asset_type", selectedOption.value, {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              });
              setOption(selectedOption.value);
            }
          }}
          value={assetTypeValue}
        />
        {methods.formState.errors.asset_type && (
          <span className="text-red-500 text-sm mt-1">
            {methods.formState.errors.asset_type.message as string}
          </span>
        )}
      </div>
      {normalizeAssetType(option) === "sample" && (
        <>
          <Input name="issueDate" type="date" label="Issue Date" required />
          <Input name="returnDate" type="date" label="Return Date" required />
        </>
      )}
      {normalizeAssetType(option) === "own" && (
        <>
          <Input
            name="cost"
            type="number"
            label="Cost"
            placeholder="Eg: 5000"
            required
          />
          <Input
            name="depreciationRate"
            type="number"
            label="Depreciation Rate"
            placeholder="Eg: 10.0"
            required
          />
        </>
      )}
      {normalizeAssetType(option) === "rental" && (
        <>
          <Input
            name="rentAmount"
            type="number"
            label="Rental Amount"
            placeholder="Eg: 5000"
            required
          />
          <Input
            name="rentStartDate"
            type="date"
            label="Rent Start Date"
            required
          />
        </>
      )}

      {/* Full-width row so the upload tile + selected files sit on their own line
          with room for all tiles (no wrapping → no layout jump on add/remove). */}
      <div className="sm:col-span-3">
        <InventoryImageUploader
          items={images}
          onAddFiles={handleAddFiles}
          onRemove={removeImage}
          accept={uploadConfig.accept}
          helpText={uploadConfig.helpText}
          maxFiles={uploadConfig.maxFiles}
          error={fileError}
        />
      </div>
      <div className="flex justify-center">
        {!isEmpty(methods.formState.errors) && (
          <span className="text-red-500 text-sm mt-1">
            Please check mandatory fields{" "}
          </span>
        )}
      </div>
    </div>,
  ];

  useEffect(() => {
    setSelected({
      value: data.userEmail,
      label: data.assign,
    });
  }, []);

  useEffect(() => {
    if (!usersInitialized) {
      setLoading(true);
    }

    const timer = window.setTimeout(() => {
      void fetchUsers(0, userSearch, false)
        .finally(() => {
          if (!usersInitialized) {
            setLoading(false);
            setUsersInitialized(true);
          }
        });
    }, 400);

    return () => window.clearTimeout(timer);
  }, [userSearch, usersInitialized]);

  // Combined initialization blocks and reset dirty state so changes can be tracked correctly
  useEffect(() => {
    methods.setValue("assignDate", toDateInputValue(data.assignDate));
    methods.setValue("purchaseDate", toDateInputValue(data.purchaseDate));
    methods.setValue("releaseDate", toDateInputValue(data.releaseDate));
    methods.setValue("type", data.type);

    setOption(data.asset_type);
    setDevice(data.type);

    const DeviceType = data.asset_type;
    if (normalizeAssetType(DeviceType) === "own") {
      methods.setValue("cost", data.ownedDevice?.cost);
      methods.setValue("depreciationRate", data.ownedDevice?.depreciationRate);
    } else if (normalizeAssetType(DeviceType) === "rental") {
      methods.setValue("rentStartDate", toDateInputValue(data.rentedDevice?.rentStartDate));
      methods.setValue("rentAmount", data.rentedDevice?.rentAmount);
    } else if (normalizeAssetType(DeviceType) === "sample") {
      methods.setValue("issueDate", toDateInputValue(data.sampleDevice?.issueDate));
      methods.setValue("returnDate", toDateInputValue(data.sampleDevice?.returnDate));
    }

    // Crucial for resetting the form's dirty check baseline to these formatted values
    methods.reset(methods.getValues());
  }, []);

  // Determine if images have been added or removed
  const isImagesDirty =
    images.length !== (data.urls || []).length ||
    images.some((img: any) => img instanceof File);

  // Form is dirty if RHF states it is dirty OR the custom images array is dirty
  const isFormDirty = methods.formState.isDirty || isImagesDirty;
  // const isSubmitDisabled = !isFormDirty || isSubmitting;

  return (
    <div className="bg-transparent sm:w-full rounded-xl">
      <FormProvider {...methods}>
        {loading ? (
          <Loader />
        ) : (
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            {steps[step]}
            <div className="flex justify-center mt-4 gap-2">
              {step > 0 && (
                <button
                  type="button"
                  onClick={prevStep}
                  style={{ backgroundColor: "#64748b" }}
                  className="hover:bg-slate-600 transition-all mr-2 text-white px-5 py-2 rounded-xl font-bold shadow-lg shadow-slate-200 dark:shadow-none active:scale-95 opacity-100 visible"
                >
                  Back
                </button>
              )}
              {step < steps.length - 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ backgroundColor: "#64748b" }}
                    className="hover:bg-slate-600 transition-all mr-2 text-white px-5 py-2 rounded-xl font-bold shadow-lg shadow-slate-200 dark:shadow-none active:scale-95 opacity-100 visible"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={nextStep}
                    style={{ backgroundColor: colors.primary }}
                    className="transition-all text-white px-5 py-2 rounded-xl font-bold shadow-lg hover:opacity-90 active:scale-95"
                  >
                    Next
                  </button>
                </div>
              )}
              {step === steps.length - 1 && (
                <div className="flex justify-center gap-2">
                  <button
                    type="button"
                    onClick={onClose}
                    style={{ backgroundColor: "#64748b" }}
                    className="hover:bg-slate-600 transition-all mr-2 text-white px-5 py-2 rounded-xl font-bold shadow-lg shadow-slate-200 dark:shadow-none active:scale-95 opacity-100 visible"
                  >
                    Cancel
                  </button>
                  {/*
                    Disable Submit when the form is unchanged OR while a file
                    validation error is active, so an invalid file selection
                    cannot be submitted.
                  */}
                  <button
                    type="submit"
                    disabled={!isFormDirty || !!fileError}
                    style={{ backgroundColor: !isFormDirty || fileError ? "#94a3b8" : colors.primary }}
                    className={`transition-all text-white px-5 py-2 rounded-xl font-bold shadow-lg ${!isFormDirty || fileError ? "opacity-50 cursor-not-allowed" : "hover:opacity-90 active:scale-95"
                      }`}
                  >
                    Submit
                  </button>
                </div>
              )}
            </div>
          </form>
        )}
      </FormProvider>
      {/* {previewProps.isOpen && <MediaPreview {...previewProps} />} */}
    </div>
  );
};

export default EditInventory;